import { gradientBorderCss, safeGradient } from "@/shared/utils/gradient-css";
import { isGradient } from "@components/color-picker/lib/detect";
import styled from "styled-components";
import { resolveBorderWidthCss } from "../button/utils/border-width";

type ButtonElementProps = {
  $round: string;
  $color: string;
  $colorHover: string;
  $background: string;
  $backgroundHover: string;
  $fontSize: string;
  $horizontalAlignment: string;
  $borderColor: string;
  $borderColorHover: string;
  $borderWidth: string;
  $isBold: boolean;
};

// Legacy entities sometimes persisted the string "none" for hover colors
// (pre-design-system). Treat that the same as "unset" so we don't render
// `color: none` (invalid CSS) and instead fall back to the design-system var.
const getDesignSystemValue = (value: string, variable: string) => {
  if (value === "design-system" || !value) {
    return `var(${variable})`;
  }
  if (value === "none") return "transparent";
  return value;
};

export const ButtonElement = styled.div<ButtonElementProps>`
  &.buttonElement {
    justify-content: ${({ $horizontalAlignment }) =>
      $horizontalAlignment || "inherit"};
    .buttonWrapper {
      background: ${({ $background }) => {
        const v = getDesignSystemValue(
          $background,
          "--button-background-color",
        );
        if (isGradient(v)) {
          const safe = safeGradient(v);
          return safe ?? "transparent";
        }
        return v;
      }};

      border-radius: ${({ $round }) =>
        getDesignSystemValue($round, "--button-border-radius")};

      ${({ $borderColor, $borderWidth }) => {
        const v = getDesignSystemValue($borderColor, "--button-border-color");
        const w = resolveBorderWidthCss($borderWidth);
        if (isGradient(v)) return gradientBorderCss(v, w);
        return `border-color: ${v}; border-width: ${w};`;
      }}
      font-size: ${({ $fontSize }) =>
        $fontSize ? `${$fontSize}px` : "var(--paragraph-size)"} !important;
      font-weight: ${({ $isBold }) =>
        $isBold ? "bold" : "inherit"} !important;

      color: ${({ $color }) =>
        getDesignSystemValue($color, "--button-text-color")} !important;
      p,
      span,
      svg {
        color: ${({ $color }) =>
          getDesignSystemValue($color, "--button-text-color")} !important;
        font-size: ${({ $fontSize }) =>
          $fontSize ? `${$fontSize}px` : "var(--paragraph-size)"} !important;
        font-weight: ${({ $isBold }) =>
          $isBold ? "bold" : "inherit"} !important;
      }
      &:hover {
        color: ${({ $colorHover }) =>
          getDesignSystemValue(
            $colorHover,
            "--button-text-hover-color",
          )} !important;
        background: ${({ $backgroundHover }) => {
          const v = getDesignSystemValue(
            $backgroundHover,
            "--button-background-hover-color",
          );
          if (isGradient(v)) {
            const safe = safeGradient(v);
            return safe ? `${safe} !important` : "transparent !important";
          }
          return `${v} !important`;
        }};
        ${({ $borderColorHover, $borderWidth }) => {
          const v = getDesignSystemValue(
            $borderColorHover,
            "--button-border-color-hover",
          );
          const w = resolveBorderWidthCss($borderWidth);
          if (isGradient(v)) {
            const safe = safeGradient(v);
            if (!safe) {
              return `border-color: transparent !important; &::before { content: none; }`;
            }
            return `
              border-color: transparent !important;
              &::before {
                content: '';
                position: absolute;
                inset: calc(-1 * ${w});
                border-radius: inherit;
                border: ${w} solid transparent;
                background: ${safe} border-box;
                -webkit-mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
                -webkit-mask-composite: xor;
                mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
                mask-composite: exclude;
                pointer-events: none;
              }
            `;
          }
          return `border-color: ${v} !important; &::before { content: none; }`;
        }}
        p,
        span,
        svg {
          color: ${({ $colorHover }) =>
            getDesignSystemValue(
              $colorHover,
              "--button-text-hover-color",
            )} !important;
          font-weight: ${({ $isBold }) =>
            $isBold ? "bold" : "inherit"} !important;
        }
      }
    }
  }
`;

export const Wrapper = styled.div<{ $align: string }>`
  display: flex;
  justify-content: ${({ $align }) => $align || "inherit"};
`;
